import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Print, Close, PictureAsPdf, Add, Remove, RestartAlt } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { AEstimate } from '../../utils/estimate.types';
import { Utils } from '../../utils/common-utils';

interface ThermalPrintDialogProps {
  open: boolean;
  onClose: () => void;
  invoice: AEstimate;
  businessName: string;
  businessPhone: string;
  businessAddress: string;
}

const paperSizes = [
  { width: 58, name: '58mm (Thermal)' },
  { width: 80, name: '80mm (Thermal)' },
];

const ThermalPrintDialog: React.FC<ThermalPrintDialogProps> = ({
  open,
  onClose,
  invoice,
  businessName,
  businessPhone,
  businessAddress,
}) => {
  const [selectedSize, setSelectedSize] = useState(paperSizes[0]);
  const [scale, setScale] = useState(1);
  const [previewScale, setPreviewScale] = useState(1);
  const previewRef = useRef<HTMLDivElement>(null);

  const generateReceiptContent = useCallback(() => {
    return (
      <div 
        style={{
          width: `${selectedSize.width}mm`,
          minHeight: '100mm',
          fontFamily: 'monospace',
          fontSize: '8pt',
          padding: '2mm',
          boxSizing: 'border-box',
          backgroundColor: 'white',
          boxShadow: '0 0 5px rgba(0,0,0,0.2)',
          margin: '0 auto',
        }}
      >
        <div style={{ textAlign: 'center', borderBottom: '1px dashed #000', paddingBottom: '2mm', marginBottom: '2mm' }}>
          <div style={{ fontWeight: 'bold', fontSize: '10pt', marginBottom: '1mm' }}>{businessName}</div>
          {businessAddress && <div>{businessAddress}</div>}
          {businessPhone && <div>Ph: {businessPhone}</div>}
          <div style={{ margin: '2mm 0' }}>--------------------------------</div>
          <div><strong>{invoice.voucherType?.toUpperCase()}</strong></div>
                      <div>Bill: ${(invoice as any).invoiceNumber || 'N/A'}</div>
          <div>{new Date().toLocaleDateString('en-IN')} {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
        </div>
        
        <div style={{ marginBottom: '2mm', borderBottom: '1px dashed #ccc', paddingBottom: '2mm' }}>
          <div><strong>BILL TO:</strong></div>
          <div>{invoice.client?.name || invoice.supplier?.name || 'Customer'}</div>
          {invoice.client?.phone || invoice.supplier?.phone ? (
            <div>{invoice.client?.phone || invoice.supplier?.phone}</div>
          ) : null}
        </div>
        
        <div style={{ marginBottom: '2mm' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1mm' }}>
            <div style={{ flex: '2', fontWeight: 'bold' }}>DESCRIPTION</div>
            <div style={{ flex: '1', textAlign: 'right', fontWeight: 'bold' }}>AMOUNT</div>
          </div>
          
          {(invoice as any).lineItems?.map((item: any, i: number) => (
            <div key={i} style={{ marginBottom: '1mm', display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ flex: '2' }}>
                <div>{item.description || item.name || `Item ${i + 1}`}</div>
                <div style={{ fontSize: '7pt', color: '#666' }}>
                  {item.qty || 1} x {Utils.formatNumber(item.appliedRate || item.rate || 0)}
                </div>
              </div>
              <div style={{ flex: '1', textAlign: 'right' }}>
                {Utils.formatNumber(item.total || 0)}
              </div>
            </div>
          )) || (
            <div style={{ marginBottom: '1mm', display: 'flex', justifyContent: 'space-between' }}>
              <div>Service</div>
              <div>{Utils.formatNumber(invoice.amount || 0)}</div>
            </div>
          )}
        </div>
        
        <div style={{ 
          textAlign: 'right', 
          margin: '2mm 0', 
          padding: '2mm', 
          borderTop: '1px dashed #000',
          borderBottom: '1px dashed #000',
          fontWeight: 'bold'
        }}>
          TOTAL: ₹{Utils.formatNumber(invoice.amount || 0)}
        </div>
        
        <div style={{ 
          textAlign: 'center', 
          marginTop: '4mm',
          paddingTop: '2mm',
          borderTop: '1px dashed #000',
          fontSize: '7pt',
          color: '#666'
        }}>
          <div>Thank you for your business!</div>
          <div>Powered by Wareozo</div>
        </div>
      </div>
    );
  }, [invoice, businessName, businessAddress, businessPhone, selectedSize]);

  const generateReceiptHTML = useCallback(() => {
    const itemsHTML = (invoice as any).lineItems?.map((item: any, i: number) => `
      <div style="margin-bottom: 1mm; display: flex; justify-content: space-between">
        <div style="flex: 2">
          <div>${item.description || item.name || `Item ${i + 1}`}</div>
          <div style="font-size: 7pt; color: #666">
            ${item.qty || 1} x ${Utils.formatNumber(item.appliedRate || item.rate || 0)}
          </div>
        </div>
        <div style="flex: 1; text-align: right">
          ${Utils.formatNumber(item.total || 0)}
        </div>
      </div>
    `).join('') || `
      <div style="margin-bottom: 1mm; display: flex; justify-content: space-between">
        <div>Service</div>
        <div>${Utils.formatNumber(invoice.amount || 0)}</div>
      </div>
    `;

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt</title>
          <style>
            body { 
              margin: 0;
              padding: 2mm; /* This padding creates the margin effect */
              font-family: monospace;
              font-size: 8pt;
              line-height: 1.3;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              box-sizing: border-box;
            }
          </style>
        </head>
        <body>
        
          <div>
            <div style="text-align: center; border-bottom: 1px dashed #000; padding-bottom: 2mm; margin-bottom: 2mm">
              <div style="font-weight: bold; font-size: 10pt; margin-bottom: 1mm;">${businessName}</div>
              ${businessAddress ? `<div>${businessAddress}</div>` : ''}
              ${businessPhone ? `<div>Ph: ${businessPhone}</div>` : ''}
              <div style="margin: 2mm 0">--------------------------------</div>
              <div><strong>${invoice.voucherType?.toUpperCase() || 'RECEIPT'}</strong></div>
                          <div>Bill: ${(invoice as any).invoiceNumber || 'N/A'}</div>
              <div>${new Date().toLocaleDateString('en-IN')} ${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
            
            <div style="margin-bottom: 2mm; border-bottom: 1px dashed #ccc; padding-bottom: 2mm">
              <div><strong>BILL TO:</strong></div>
              <div>${invoice.client?.name || invoice.supplier?.name || 'Customer'}</div>
              ${invoice.client?.phone || invoice.supplier?.phone ? `<div>${invoice.client?.phone || invoice.supplier?.phone}</div>` : ''}
            </div>
            
            <div style="margin-bottom: 2mm">
              <div style="display: flex; justify-content: space-between; margin-bottom: 1mm">
                <div style="flex: 2; font-weight: bold">DESCRIPTION</div>
                <div style="flex: 1; text-align: right; font-weight: bold">AMOUNT</div>
              </div>
              ${itemsHTML}
            </div>
            
            <div style="text-align: right; margin: 2mm 0; padding: 2mm; border-top: 1px dashed #000; border-bottom: 1px dashed #000; font-weight: bold">
              TOTAL: ₹${Utils.formatNumber(invoice.amount || 0)}
            </div>
            
            <div style="text-align: center; margin-top: 4mm; padding-top: 2mm; border-top: 1px dashed #000; font-size: 7pt; color: #666">
              <div>Thank you for your business!</div>
              <div>Powered by Wareozo</div>
            </div>
          </div>
        </body>
      </html>
    `;
  }, [invoice, businessName, businessAddress, businessPhone, selectedSize]);

  const handlePrint = () => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.left = '-9999px';
    
    document.body.appendChild(iframe);
    
    const printContent = generateReceiptHTML();
    
    if (!iframe.contentWindow) {
      toast.error('Failed to initialize print window');
      document.body.removeChild(iframe);
      return;
    }
    
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(printContent);
    doc.close();

    iframe.onload = () => {
      setTimeout(() => {
        if (iframe.contentWindow && iframe.contentDocument) {
          const contentHeightPx = iframe.contentDocument.body.scrollHeight;
          const contentHeightMm = contentHeightPx * 0.264583;

          const style = doc.createElement('style');
          style.innerHTML = `
            @page {
              size: ${selectedSize.width}mm ${contentHeightMm + 5}mm;
              margin: 0;
            }
          `;
          doc.head.appendChild(style);
          
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 500);
        }
      }, 250);
    };

    iframe.onerror = () => {
      toast.error('Failed to prepare print content.');
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
    };
  };

  const handleSaveAsPDF = () => {
    alert('PDF generation would be implemented here. For now, you can use the system print dialog and select "Save as PDF" as the printer.');
  };

  useEffect(() => {
    const updateScale = () => {
      if (previewRef.current) {
        const containerWidth = previewRef.current.offsetWidth - 40;
        const scale = Math.min(1, containerWidth / (selectedSize.width * 3.78));
        setPreviewScale(scale);
      }
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [selectedSize]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ style: { height: '90vh', maxHeight: '800px' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Print Preview</Typography>
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined" size="small" margin="normal">
              <InputLabel>Paper Size</InputLabel>
              <Select
                value={selectedSize.name}
                onChange={(e) => {
                  const size = paperSizes.find(s => s.name === e.target.value) || paperSizes[0];
                  setSelectedSize(size);
                }}
                label="Paper Size"
              >
                {paperSizes.map((size) => (
                  <MenuItem key={size.name} value={size.name}>
                    {size.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>Print Options:</Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Button variant="contained" color="primary" startIcon={<Print />} onClick={handlePrint} fullWidth>
                  Print Now
                </Button>
                <Button variant="outlined" color="secondary" startIcon={<PictureAsPdf />} onClick={handleSaveAsPDF} fullWidth>
                  Save as PDF
                </Button>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={9}>
            <Box 
              ref={previewRef}
              display="flex" 
              flexDirection="column"
              alignItems="center" 
              height="100%"
              bgcolor="#f5f5f5"
              p={2}
              borderRadius={1}
              overflow="auto"
            >
              <Box
                style={{
                  transform: `scale(${previewScale * scale})`,
                  transformOrigin: 'top center',
                  transition: 'transform 0.2s',
                  width: `${selectedSize.width}mm`,
                  minHeight: '100mm',
                  backgroundColor: 'white',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                }}
              >
                {generateReceiptContent()}
              </Box>
              
              <Box 
                mt={2} 
                display="flex" 
                alignItems="center"
                justifyContent="center"
                gap={1}
                p={1}
                borderRadius={2}
                bgcolor="background.paper"
                boxShadow={1}
              >
                <Tooltip title="Zoom Out">
                  <span> 
                    <IconButton size="small" onClick={() => setScale(prev => Math.max(0.5, prev - 0.1))} disabled={scale <= 0.5}>
                      <Remove />
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography variant="body2" color="textSecondary" sx={{ minWidth: '50px', textAlign: 'center' }}>
                  {Math.round(scale * 100)}%
                </Typography>
                <Tooltip title="Zoom In">
                  <span> 
                    <IconButton size="small" onClick={() => setScale(prev => Math.min(2, prev + 0.1))} disabled={scale >= 2}>
                      <Add />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Reset Zoom">
                   <IconButton size="small" onClick={() => setScale(1)}>
                      <RestartAlt />
                    </IconButton>
                </Tooltip>
              </Box>
              
              <Box mt={1} textAlign="center">
                <Typography variant="caption" color="textSecondary">
                  Paper Size: {selectedSize.width}mm
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ThermalPrintDialog;